"use client";

import { SearchZoomIn, SearchZoomOut, TickCircle } from "iconsax-reactjs";
import { useTranslations } from "next-intl";
import { useCallback, useEffect, useState } from "react";
import Cropper, { type Area, type Point } from "react-easy-crop";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { blobToFile, getCroppedImg } from "@/lib/utils/crop-image";

export interface ImageItem {
  id: string;
  originalUrl: string;
  fileName: string;
  croppedFile?: File;
}

export interface MultiImageCropDialogProps {
  /** Array of images to crop */
  images: ImageItem[];
  /** Aspect ratio for the crop area (default: 1 for square) */
  aspectRatio?: number;
  /** Shape of the crop area */
  cropShape?: "rect" | "round";
  /** Title shown in the dialog header */
  title?: string;
  /** Whether the dialog is open */
  open: boolean;
  /** Callback when dialog open state changes */
  onOpenChange: (open: boolean) => void;
  /** Callback when all crops are confirmed */
  onCropComplete: (croppedFiles: File[], originalUrls: string[]) => void;
  /** Minimum zoom level (default: 1) */
  minZoom?: number;
  /** Maximum zoom level (default: 3) */
  maxZoom?: number;
  /** Maximum optimized output size in MB */
  maxSizeMB?: number;
}

interface CropState {
  crop: Point;
  zoom: number;
  croppedAreaPixels: Area | null;
}

export const MultiImageCropDialog = ({
  images,
  aspectRatio = 1,
  cropShape = "rect",
  title,
  open,
  onOpenChange,
  onCropComplete,
  minZoom = 1,
  maxZoom = 3,
  maxSizeMB = 5,
}: MultiImageCropDialogProps) => {
  const t = useTranslations("common.imageCrop");
  const resolvedTitle = title ?? t("cropImages");
  // Track current selected image index
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Track crop state for each image
  const [cropStates, setCropStates] = useState<Map<string, CropState>>(
    new Map(),
  );

  // Track which images have been cropped
  const [croppedImages, setCroppedImages] = useState<Map<string, File>>(
    new Map(),
  );

  const [isProcessing, setIsProcessing] = useState(false);

  // Initialize crop states when images change
  useEffect(() => {
    if (images.length > 0) {
      const newCropStates = new Map<string, CropState>();
      images.forEach((img) => {
        if (!cropStates.has(img.id)) {
          newCropStates.set(img.id, {
            crop: { x: 0, y: 0 },
            zoom: 1,
            croppedAreaPixels: null,
          });
        } else {
          newCropStates.set(img.id, cropStates.get(img.id)!);
        }
      });
      setCropStates(newCropStates);
    }
  }, [images]);

  // Reset when dialog opens
  useEffect(() => {
    if (open) {
      setSelectedIndex(0);
      setCroppedImages(new Map());
      const newCropStates = new Map<string, CropState>();
      images.forEach((img) => {
        newCropStates.set(img.id, {
          crop: { x: 0, y: 0 },
          zoom: 1,
          croppedAreaPixels: null,
        });
      });
      setCropStates(newCropStates);
    }
  }, [open, images]);

  const currentImage = images[selectedIndex];
  const currentCropState = currentImage
    ? cropStates.get(currentImage.id)
    : null;

  const onCropChange = useCallback(
    (location: Point) => {
      if (!currentImage) return;
      setCropStates((prev) => {
        const newMap = new Map(prev);
        const existing = newMap.get(currentImage.id) || {
          crop: { x: 0, y: 0 },
          zoom: 1,
          croppedAreaPixels: null,
        };
        newMap.set(currentImage.id, { ...existing, crop: location });
        return newMap;
      });
    },
    [currentImage],
  );

  const onZoomChange = useCallback(
    (newZoom: number) => {
      if (!currentImage) return;
      setCropStates((prev) => {
        const newMap = new Map(prev);
        const existing = newMap.get(currentImage.id) || {
          crop: { x: 0, y: 0 },
          zoom: 1,
          croppedAreaPixels: null,
        };
        newMap.set(currentImage.id, { ...existing, zoom: newZoom });
        return newMap;
      });
    },
    [currentImage],
  );

  const handleCropAreaChange = useCallback(
    (_croppedArea: Area, croppedAreaPixels: Area) => {
      if (!currentImage) return;
      setCropStates((prev) => {
        const newMap = new Map(prev);
        const existing = newMap.get(currentImage.id) || {
          crop: { x: 0, y: 0 },
          zoom: 1,
          croppedAreaPixels: null,
        };
        newMap.set(currentImage.id, { ...existing, croppedAreaPixels });
        return newMap;
      });
    },
    [currentImage],
  );

  // Crop the current image
  const handleCropCurrent = async () => {
    if (!currentImage || !currentCropState?.croppedAreaPixels) return;

    setIsProcessing(true);
    try {
      // Crop, resize, and convert to WebP
      const croppedBlob = await getCroppedImg(
        currentImage.originalUrl,
        currentCropState.croppedAreaPixels,
        0,
        { horizontal: false, vertical: false },
        {
          maxWidth: 2048,
          maxHeight: 2048,
          quality: 0.88,
          format: "webp",
          maxSizeMB,
        },
      );
      if (croppedBlob) {
        const timestamp = Date.now();
        const baseName = currentImage.fileName.replace(/\.[^/.]+$/, "");
        const fileName = `${baseName}-cropped-${timestamp}`;

        // blobToFile will add .webp extension
        const croppedFile = blobToFile(croppedBlob, fileName, "webp");

        setCroppedImages((prev) => {
          const newMap = new Map(prev);
          newMap.set(currentImage.id, croppedFile);
          return newMap;
        });

        // Auto-advance to next uncropped image if available
        const nextUncroppedIndex = images.findIndex(
          (img, idx) => idx > selectedIndex && !croppedImages.has(img.id),
        );
        if (nextUncroppedIndex !== -1) {
          setSelectedIndex(nextUncroppedIndex);
        }
      }
    } catch (error) {
      console.error("Error cropping image:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  // Confirm all crops
  const handleConfirmAll = () => {
    // Collect all cropped files in order
    const croppedFiles: File[] = [];
    const originalUrls: string[] = [];

    images.forEach((img) => {
      const croppedFile = croppedImages.get(img.id);
      if (croppedFile) {
        croppedFiles.push(croppedFile);
        originalUrls.push(img.originalUrl);
      }
    });

    if (croppedFiles.length > 0) {
      onCropComplete(croppedFiles, originalUrls);
    }
    onOpenChange(false);
  };

  const handleCancel = () => {
    onOpenChange(false);
  };

  // Check if all images are cropped
  const allCropped =
    images.length > 0 && images.every((img) => croppedImages.has(img.id));
  const croppedCount = croppedImages.size;

  // Zoom slider step calculation
  const zoomStep = (maxZoom - minZoom) / 100;
  const currentZoom = currentCropState?.zoom || 1;
  const currentCrop = currentCropState?.crop || { x: 0, y: 0 };

  if (!currentImage) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          "w-[calc(100vw-2rem)] max-w-md p-0 overflow-hidden",
          "sm:max-w-xl",
        )}
      >
        <DialogHeader className="px-4 pt-4 pb-2">
          <DialogTitle className="text-center text-text-primary">
            {resolvedTitle} ({croppedCount}/{images.length})
          </DialogTitle>
          <DialogDescription className="sr-only">
            {t("descMulti")}
          </DialogDescription>
        </DialogHeader>

        {/* Thumbnail Strip */}
        <div className="px-4 pb-2">
          <div className="flex gap-2 overflow-x-auto py-2 scrollbar-thin">
            {images.map((img, index) => {
              const isCropped = croppedImages.has(img.id);
              const isSelected = index === selectedIndex;

              return (
                <button
                  key={img.id}
                  type="button"
                  onClick={() => setSelectedIndex(index)}
                  className={cn(
                    "relative shrink-0 w-16 h-16 rounded-sm overflow-hidden transition-all",
                    "border-2",
                    isSelected
                      ? "border-accent ring-2 ring-accent/30"
                      : isCropped
                        ? "border-semantic-success"
                        : "border-stroke-light hover:border-stroke-primary",
                  )}
                >
                  {/* biome-ignore lint: img needed for data URLs */}
                  <img
                    src={img.originalUrl}
                    alt={t("imageIndex", { index: index + 1 })}
                    className="w-full h-full object-cover"
                  />
                  {/* Cropped indicator */}
                  {isCropped && (
                    <div className="absolute inset-0 bg-semantic-success/30 flex items-center justify-center">
                      <TickCircle
                        size={24}
                        variant="Bold"
                        className="text-semantic-success"
                      />
                    </div>
                  )}
                  {/* Index badge */}
                  <span
                    className={cn(
                      "absolute bottom-0.5 right-0.5 text-[10px] font-bold px-1 rounded-xs",
                      isSelected
                        ? "bg-accent text-text-button-label"
                        : "bg-bg-primary/80 text-text-primary",
                    )}
                  >
                    {index + 1}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Cropper Container */}
        <div
          className={cn(
            "relative w-full bg-bg-primary",
            "h-[250px] sm:h-[300px]",
          )}
        >
          <Cropper
            image={currentImage.originalUrl}
            crop={currentCrop}
            zoom={currentZoom}
            aspect={aspectRatio}
            cropShape={cropShape}
            showGrid={cropShape === "rect"}
            onCropChange={onCropChange}
            onCropComplete={handleCropAreaChange}
            onZoomChange={onZoomChange}
            minZoom={minZoom}
            maxZoom={maxZoom}
            objectFit="contain"
            classes={{
              containerClassName: "!bg-bg-primary",
              cropAreaClassName: cn(
                cropShape === "round"
                  ? "!border-2 !border-accent"
                  : "!border-2 !border-dashed !border-accent",
              ),
            }}
          />
        </div>

        {/* Zoom Controls */}
        <div className="px-4 py-3 space-y-2">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => onZoomChange(Math.max(minZoom, currentZoom - 0.1))}
              className="p-1.5 rounded-xs bg-bg-tertiary/60 text-text-primary hover:bg-bg-tertiary transition-colors"
              aria-label={t("zoomOut")}
            >
              <SearchZoomOut size={20} />
            </button>

            {/* Custom Range Slider */}
            <div className="flex-1 relative h-8 flex items-center">
              <div className="absolute inset-x-0 h-1.5 bg-bg-tertiary rounded-full" />
              <div
                className="absolute h-1.5 bg-accent rounded-full"
                style={{
                  width: `${((currentZoom - minZoom) / (maxZoom - minZoom)) * 100}%`,
                }}
              />
              <input
                type="range"
                min={minZoom}
                max={maxZoom}
                step={zoomStep}
                value={currentZoom}
                onChange={(e) => onZoomChange(Number(e.target.value))}
                className={cn(
                  "absolute inset-x-0 w-full h-8 appearance-none bg-transparent cursor-pointer",
                  "[&::-webkit-slider-thumb]:appearance-none",
                  "[&::-webkit-slider-thumb]:w-5",
                  "[&::-webkit-slider-thumb]:h-5",
                  "[&::-webkit-slider-thumb]:rounded-full",
                  "[&::-webkit-slider-thumb]:bg-accent",
                  "[&::-webkit-slider-thumb]:border-2",
                  "[&::-webkit-slider-thumb]:border-bg-primary",
                  "[&::-webkit-slider-thumb]:shadow-md",
                  "[&::-webkit-slider-thumb]:cursor-pointer",
                  "[&::-webkit-slider-thumb]:transition-transform",
                  "[&::-webkit-slider-thumb]:hover:scale-110",
                  "[&::-moz-range-thumb]:w-5",
                  "[&::-moz-range-thumb]:h-5",
                  "[&::-moz-range-thumb]:rounded-full",
                  "[&::-moz-range-thumb]:bg-accent",
                  "[&::-moz-range-thumb]:border-2",
                  "[&::-moz-range-thumb]:border-bg-primary",
                  "[&::-moz-range-thumb]:shadow-md",
                  "[&::-moz-range-thumb]:cursor-pointer",
                  "[&::-webkit-slider-runnable-track]:bg-transparent",
                  "[&::-moz-range-track]:bg-transparent",
                )}
                aria-label={t("zoomLevel")}
              />
            </div>

            <button
              type="button"
              onClick={() => onZoomChange(Math.min(maxZoom, currentZoom + 0.1))}
              className="p-1.5 rounded-xs bg-bg-tertiary/60 text-text-primary hover:bg-bg-tertiary transition-colors"
              aria-label={t("zoomIn")}
            >
              <SearchZoomIn size={20} />
            </button>
          </div>

          <p className="text-xs text-text-secondary text-center">
            {t("multiClickHint")}
          </p>
        </div>

        <DialogFooter className="px-4 pb-4 flex-row gap-3">
          <Button
            type="button"
            variant="ghost"
            colors="secondary"
            onClick={handleCancel}
            className="flex-1"
            disabled={isProcessing}
          >
            {t("cancel")}
          </Button>

          {/* Crop current button */}
          {!croppedImages.has(currentImage.id) ? (
            <Button
              type="button"
              variant="solid"
              colors="secondary"
              onClick={handleCropCurrent}
              className="flex-1"
              loading={isProcessing}
              disabled={!currentCropState?.croppedAreaPixels}
            >
              {t("cropThis")}
            </Button>
          ) : (
            <Button
              type="button"
              variant="faded"
              colors="secondary"
              onClick={handleCropCurrent}
              className="flex-1"
              loading={isProcessing}
            >
              {t("recrop")}
            </Button>
          )}

          {/* Confirm all button - only enabled when all are cropped */}
          <Button
            type="button"
            variant="solid"
            colors="primary"
            onClick={handleConfirmAll}
            className="flex-1"
            disabled={!allCropped || isProcessing}
          >
            {t("confirmAll", { done: croppedCount, total: images.length })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
